const CELLPHONE_FIRST_DIGITS = ['5', '6', '7', '8', '9']

export const FIXED_CAMPAIGN_CONTACTS = [
  { NUMERO: '5521996895012', NOME: 'LUCAS' },
  { NUMERO: '5521965406454', NOME: 'FELIPE' },
  { NUMERO: '5518997083364', NOME: 'FERNANDA' },
]

export const NAME_VARIABLE_KEYS = new Set([
  'NOME',
  'NAME',
  'CLIENTE',
  'CUSTOMER',
])

/**
 * Padroniza telefone BR:
 * - só dígitos
 * - inclui DDI 55 se faltar
 * - inclui 9º dígito em celular se faltar
 * Ex.: 21996895012 -> 5521996895012 | 2196895012 -> 5521996895012
 */
export function normalizeBrazilPhone(phone) {
  let digits = String(phone || '').replace(/\D/g, '')
  if (!digits) return ''

  if (!digits.startsWith('55')) {
    digits = `55${digits}`
  }

  // 55 + DDD(2) + número(8) = 12 → celular antigo sem o 9
  if (digits.length === 12) {
    const firstLocalDigit = digits.charAt(4)
    if (CELLPHONE_FIRST_DIGITS.includes(firstLocalDigit)) {
      digits = `${digits.slice(0, 4)}9${digits.slice(4)}`
    }
  }

  return digits
}

function normalizeHeaderKey(header) {
  return String(header || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .replace(/[^\w]/g, '')
    .toUpperCase()
}

function detectDelimiter(line) {
  return ['|', ';', '\t', ','].find(char => String(line || '').includes(char)) || ','
}

function splitCsvLine(line, delimiter) {
  return String(line || '')
    .split(delimiter)
    .map(part => part.trim())
}

function isPhoneHeader(header) {
  const key = normalizeHeaderKey(header)
  return [
    'TELEFONE',
    'PHONE',
    'CELULAR',
    'NUMERO',
    'NUMEROWHATSAPP',
    'TELEFONELIMPO',
    'CELULARLIMPO',
    'NUMEROLIMPO',
  ].includes(key) || key.includes('TELEFONE') || key.includes('NUMERO') || key.includes('PHONE') || key.includes('CELULAR')
}

function getContactPhone(contact) {
  if (!contact) return ''
  if (typeof contact === 'string' || typeof contact === 'number') {
    return normalizeBrazilPhone(contact)
  }

  const raw =
    contact.NUMERO ||
    contact.numero ||
    contact.phone ||
    contact.telefone ||
    contact.TELEFONE ||
    contact.CELULAR ||
    ''

  return normalizeBrazilPhone(raw)
}

function findNameVariableKey(variableKeys = []) {
  return variableKeys.find(key => NAME_VARIABLE_KEYS.has(String(key).toUpperCase())) || null
}

function pickRandomItem(items = []) {
  if (!items.length) return null
  const index = Math.floor(Math.random() * items.length)
  return items[index]
}

function collectVariableSamples(sourceContacts = [], variableKeys = []) {
  const samples = {}

  variableKeys.forEach(key => {
    const values = []
    sourceContacts.forEach(contact => {
      if (!contact || typeof contact !== 'object') return
      const value = String(contact[key] ?? '').trim()
      if (value) values.push(value)
    })
    samples[key] = values
  })

  return samples
}

function looksNumericVariable(key = '', samples = []) {
  const upper = String(key).toUpperCase()
  if (/(CODIGO|CODE|ID|CPF|CNPJ|CEP|NUM|QTD|VALOR|VAL|SKU)/.test(upper)) {
    return true
  }

  if (!samples.length) return false
  return samples.every(value => /^\d+$/.test(String(value).trim()))
}

function generateRandomVariableValue(key = '', samples = []) {
  const sampled = pickRandomItem(samples)
  if (sampled !== null && sampled !== undefined) {
    return String(sampled)
  }

  if (looksNumericVariable(key, samples)) {
    return String(100000 + Math.floor(Math.random() * 900000))
  }

  const suffix = Math.random().toString(36).slice(2, 7).toUpperCase()
  return `TESTE_${suffix}`
}

/**
 * Monta os contatos fixos no formato da base.
 * - Se houver coluna de nome, preenche com LUCAS/FELIPE/FERNANDA
 * - Demais variáveis: valor aleatório da própria base; se não houver, gera texto/número
 * - asPlainString=true devolve só o telefone (voz)
 */
export function buildFixedCampaignContacts(
  variableKeys = [],
  { asPlainString = false, sourceContacts = [] } = {},
) {
  const keys = (variableKeys || [])
    .map(key => String(key).toUpperCase())
    .filter(key => key && key !== 'NUMERO')
  const nameKey = findNameVariableKey(keys)
  const samplesByKey = collectVariableSamples(sourceContacts, keys)

  return FIXED_CAMPAIGN_CONTACTS.map(fixed => {
    if (asPlainString) {
      return fixed.NUMERO
    }

    const contact = { NUMERO: fixed.NUMERO }
    keys.forEach(key => {
      if (nameKey && key === nameKey) {
        contact[key] = fixed.NOME
        return
      }

      contact[key] = generateRandomVariableValue(key, samplesByKey[key] || [])
    })

    return contact
  })
}

/**
 * Injeta os números fixos sem duplicar pelo telefone normalizado.
 */
export function injectFixedCampaignContacts(contacts = [], options = {}) {
  const list = Array.isArray(contacts) ? [...contacts] : []
  const existing = new Set(
    list
      .map(contact => getContactPhone(contact))
      .filter(Boolean),
  )

  const variableKeys = options.variableKeys
    || (list.find(item => item && typeof item === 'object')
      ? Object.keys(list.find(item => item && typeof item === 'object')).filter(key => key !== 'NUMERO')
      : [])

  const fixed = buildFixedCampaignContacts(variableKeys, {
    asPlainString: options.asPlainString === true,
    sourceContacts: list,
  })

  fixed.forEach(contact => {
    const phone = getContactPhone(contact)
    if (!phone || existing.has(phone)) return
    existing.add(phone)
    list.push(contact)
  })

  return list
}

/**
 * Parseia CSV no padrão: 1ª coluna telefone, demais variáveis.
 * Aceita header opcional. Sempre devolve objetos { NUMERO, ...vars }.
 */
export function parseCampaignCsv(content) {
  const text = String(content || '').replace(/^\uFEFF/, '')
  const lines = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean)

  if (!lines.length) {
    return { contacts: [], variableKeys: [], totalRows: 0, duplicateCount: 0 }
  }

  const delimiter = detectDelimiter(lines[0])
  const firstParts = splitCsvLine(lines[0], delimiter)
  const firstCellDigits = String(firstParts[0] || '').replace(/\D/g, '')
  const hasHeader = firstCellDigits.length < 10

  let headers = []
  let dataLines = lines

  if (hasHeader) {
    headers = firstParts.map((header, index) => {
      if (index === 0 || isPhoneHeader(header)) return 'NUMERO'
      return normalizeHeaderKey(header) || `VAR_${index}`
    })
    // Garante NUMERO na primeira coluna
    headers[0] = 'NUMERO'
    dataLines = lines.slice(1)
  } else {
    headers = firstParts.map((_, index) => (index === 0 ? 'NUMERO' : `VAR_${index}`))
  }

  // Se houver header com nomes reais nas demais colunas, usa esses nomes
  if (hasHeader) {
    firstParts.forEach((header, index) => {
      if (index === 0) return
      const key = normalizeHeaderKey(header)
      if (key && key !== 'NUMERO') headers[index] = key
    })
    headers[0] = 'NUMERO'
  }

  const variableKeys = headers
    .filter((key, index) => index > 0 && key && key !== 'NUMERO')
    .filter((key, index, arr) => arr.indexOf(key) === index)

  const uniqueMap = new Map()
  let totalRows = 0

  dataLines.forEach(line => {
    const parts = splitCsvLine(line, delimiter)
    const phone = normalizeBrazilPhone(parts[0])
    if (!phone) return

    totalRows += 1
    if (uniqueMap.has(phone)) return

    const contact = { NUMERO: phone }
    variableKeys.forEach((key, index) => {
      const value = String(parts[index + 1] || '').trim()
      contact[key] = value
    })
    uniqueMap.set(phone, contact)
  })

  const contacts = Array.from(uniqueMap.values())
  const withFixed = injectFixedCampaignContacts(contacts, { variableKeys })

  return {
    contacts: withFixed,
    variableKeys,
    totalRows,
    duplicateCount: Math.max(totalRows - contacts.length, 0),
  }
}

export function isCsvFile(file) {
  if (!file) return false
  const name = String(file.name || '').toLowerCase()
  const type = String(file.type || '').toLowerCase()
  return name.endsWith('.csv') || type === 'text/csv' || type === 'application/vnd.ms-excel'
}

export function buildExampleCampaignCsv() {
  return [
    'telefone,nome',
    '21996895012,Cliente Exemplo',
    '11999999999,Outro Cliente',
  ].join('\n')
}
